import { z } from 'zod';
import { maChiSoSchema, phamViHieuSuatSchema, PHAM_VI_MAC_DINH } from './analytics.js';
import { maKySchema } from './ky.js';
import { granularitySchema } from './sync.js';
/**
 * Ba tab gộp **nhiều kênh** của trang Hiệu suất: Nội dung, Người xem, Doanh thu.
 *
 * Để riêng file này chứ không nối vào `hieuSuat.ts`: file kia là hợp đồng của
 * bốn tab **một kênh** trên trang chi tiết kênh, đã chốt ở 01b và module 11 đọc
 * một phần. Hai bộ số trả lời hai câu hỏi khác nhau nên hai hợp đồng khác nhau.
 *
 * ## Hai luật chung của mọi khối gộp
 *
 * 1. **Phần trăm gộp theo trọng số lượt xem**, không trung bình cộng các phần
 *    trăm. Kênh 2 triệu lượt với kênh 20 nghìn lượt mà mỗi kênh một phiếu bằng
 *    nhau thì con số ra không nói về người xem của công ty, nó nói về cách chia
 *    đều của phép tính.
 * 2. **Kênh không có số liệu bị loại khỏi phép gộp, và nói ra.** Mọi khối gộp
 *    đều mang `soKenhCoSoLieu` / `soKenhTrongPhamVi`; một con số gộp thiếu bốn
 *    kênh mà không nói là một con số sai.
 */
/* ------------------------------------------------------------------ */
/* Tab trên URL                                                        */
/* ------------------------------------------------------------------ */
export const TAB_HIEU_SUAT = ['tong-quan', 'noi-dung', 'nguoi-xem', 'doanh-thu'];
export const tabHieuSuatSchema = z.enum(TAB_HIEU_SUAT);
export const TAB_HIEU_SUAT_NHAN = {
    'tong-quan': 'Tổng quan',
    'noi-dung': 'Nội dung',
    'nguoi-xem': 'Người xem',
    'doanh-thu': 'Doanh thu',
};
/** Tab nào cần `revenue:view` — một luật, đọc ở cả hai phía. */
export function tabCanQuyenDoanhThu(tab) {
    return tab === 'doanh-thu';
}
/* ------------------------------------------------------------------ */
/* Truy vấn chung                                                      */
/* ------------------------------------------------------------------ */
export const hieuSuatNhieuKenhQuerySchema = z.object({
    ky: maKySchema.default('28d'),
    'pham-vi': phamViHieuSuatSchema.default(PHAM_VI_MAC_DINH),
});
/** Phần đầu chung của cả ba đáp án: khoảng kỳ và độ phủ của phép gộp. */
const dauChungSchema = z.object({
    from: z.string(),
    to: z.string(),
    prevFrom: z.string(),
    prevTo: z.string(),
    /** Số kênh **thật sự** vào phép gộp. */
    soKenhCoSoLieu: z.number().int().nonnegative(),
    /** Số kênh của phạm vi đang chọn. Lệch nhau ⇒ giao diện phải nói ra. */
    soKenhTrongPhamVi: z.number().int().nonnegative(),
    /** `true` khi kỳ có ngày YouTube chưa chốt số liệu. */
    provisional: z.boolean(),
});
/** Một ô của thanh chỉ số **không bấm được** (bốn ô đầu mỗi tab). */
export const oTomTatSchema = z.object({
    khoa: z.string(),
    nhan: z.string(),
    /** `null` = **chưa có số liệu** ⇒ giao diện hiện `—`, không hiện 0. */
    giaTri: z.number().nullable(),
    dinhDang: z.enum(['so', 'gio', 'tien', 'phan-tram', 'thoi-luong', 'rpm']),
    /** Dòng phụ dưới số: `trên 8 kênh · 12 Shorts`, `+ 4,2% tổng lượt xem`… */
    phu: z.string().nullable(),
    /** Phần trăm so kỳ trước; `null` khi không so được. */
    deltaPercent: z.number().nullable(),
    /** Chip cạnh nhãn, ví dụ `tạm tính`. */
    chip: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* Tab Nội dung                                                        */
/* ------------------------------------------------------------------ */
/**
 * Thời lượng tối đa của một Shorts, tính bằng giây.
 *
 * Nguồn: https://support.google.com/youtube/answer/10059070 — "short-form
 * videos that are up to 3 minutes long".
 *
 * ⚠️ App **suy** Shorts từ `Video.durationSec` vì bảng `videos` không có cờ
 * nào của YouTube nói video đó là Shorts hay không. Con số "M Shorts" vì vậy là
 * **ước lượng theo thời lượng**: một video dài 2 phút đăng dạng thường cũng bị
 * đếm là Shorts. Số Shorts **chính xác** nằm ở `channel_content_type_daily`
 * (`contentType = 'shorts'`), nhưng bảng đó theo ngày × kênh nên không đếm
 * được số lượng video.
 */
export const GIAY_TOI_DA_SHORTS = 180;
/** Một dòng của bảng "Video hàng đầu" gộp nhiều kênh. */
export const dongVideoHangDauSchema = z.object({
    videoId: z.string(),
    youtubeVideoId: z.string(),
    title: z.string(),
    thumbnailUrl: z.string().nullable(),
    channelId: z.string(),
    channelName: z.string(),
    views: z.number().int().nonnegative(),
    gioXem: z.number(),
    /** Thời lượng xem trung bình (giây) — Σ giờ xem ÷ Σ lượt xem của video đó. */
    averageViewDuration: z.number().int().nonnegative(),
    /** `null` = video chưa có lượt hiển thị nào ⇒ `—`. */
    ctr: z.number().nullable(),
});
/** Một loại nội dung (Video dài / Shorts / Trực tiếp). */
export const dongLoaiNoiDungSchema = z.object({
    contentType: z.string(),
    nhan: z.string(),
    views: z.number().int().nonnegative(),
    phanTram: z.number(),
});
export const noiDungNhieuKenhSchema = dauChungSchema.extend({
    o: z.array(oTomTatSchema),
    /**
     * Phễu "từ lượt hiển thị đến thời gian xem"; `null` khi không kênh nào trong
     * phạm vi có số của Reporting API cho kỳ này.
     */
    pheu: z
        .object({
        /** Ngày đầu tiên trong kỳ **có** số của Reporting API. */
        tuNgay: z.string(),
        /** `false` khi Reporting API chưa phủ hết kỳ. */
        duPhu: z.boolean(),
        impressions: z.number(),
        /** CTR = Σclick ÷ Σhiển thị của **mọi** kênh, không trung bình các CTR. */
        ctr: z.number(),
        /** Số lượt bấm, suy từ `impressions × ctr` của từng ngày từng kênh. */
        soClick: z.number(),
        viewsTuHienThi: z.number(),
        /** Phần trăm trên **tổng lượt xem** của kỳ. */
        pctViews: z.number(),
        gioXemTuHienThi: z.number(),
        /** Phần trăm trên **tổng giờ xem** của kỳ. */
        pctGioXem: z.number(),
        /** Số kênh có số Reporting API — phễu gộp trên bấy nhiêu kênh. */
        soKenh: z.number().int().nonnegative(),
    })
        .nullable(),
    theoLoaiNoiDung: z.array(dongLoaiNoiDungSchema),
    videoHangDau: z.array(dongVideoHangDauSchema),
    /** Tổng video đủ điều kiện, để nút "Xem tất cả" nói còn bao nhiêu. */
    tongVideo: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* Tab Người xem                                                       */
/* ------------------------------------------------------------------ */
export const dongQuocGiaGopSchema = z.object({
    country: z.string(),
    nhan: z.string(),
    views: z.number().int().nonnegative(),
    phanTram: z.number(),
});
export const dongDoTuoiGopSchema = z.object({
    ageGroup: z.string(),
    nhan: z.string(),
    /** Phần trăm **trên tổng người xem** của cả phạm vi, đã theo trọng số. */
    nam: z.number(),
    nu: z.number(),
    tuXacDinh: z.number(),
});
export const dongThietBiGopSchema = z.object({
    deviceType: z.string(),
    nhan: z.string(),
    /** Phần trăm theo **thời gian xem** như Studio, không theo lượt xem. */
    phanTram: z.number(),
    gioXem: z.number(),
});
export const dongTheoDangKyGopSchema = z.object({
    subscribedStatus: z.string(),
    nhan: z.string(),
    phanTram: z.number(),
    gioXem: z.number(),
});
/**
 * Một khối của tab Người xem, kèm **độ phủ riêng của chính nó**.
 *
 * Quốc gia và độ tuổi chỉ có bản lưu cho những kỳ job đã hâm nóng, nên hai khối
 * đó có thể gộp trên ít kênh hơn thiết bị và người đăng ký (hai khối kia đọc
 * bảng theo ngày, luôn đủ). Một con số `soKenhCoSoLieu` chung cho cả tab sẽ nói
 * sai về ba trong bốn khối.
 */
function khoiGop(dong) {
    return z.object({
        dong: z.array(dong),
        soKenhCoSoLieu: z.number().int().nonnegative(),
        /**
         * `true` khi khối này **chưa có bản lưu** cho kỳ đang xem.
         *
         * Khác `dong: []`: rỗng vì chưa lấy số thì giao diện phải mời chọn kỳ khác,
         * còn rỗng vì kênh thật sự không có người xem thì nói "không có số liệu".
         */
        chuaCoBanLuu: z.boolean(),
    });
}
export const nguoiXemNhieuKenhSchema = dauChungSchema.extend({
    quocGia: khoiGop(dongQuocGiaGopSchema),
    doTuoi: khoiGop(dongDoTuoiGopSchema).extend({
        /** Tổng Nam / Nữ của cả phạm vi, cho chú thích đầu khung. */
        tongNam: z.number(),
        tongNu: z.number(),
    }),
    thietBi: khoiGop(dongThietBiGopSchema),
    theoDangKy: khoiGop(dongTheoDangKyGopSchema),
});
/* ------------------------------------------------------------------ */
/* Tab Doanh thu                                                       */
/* ------------------------------------------------------------------ */
export const cotDoanhThuNgaySchema = z.object({
    date: z.string(),
    /** `null` = khoảng đó **chưa có** doanh thu khả dụng ⇒ không vẽ cột. */
    giaTri: z.number().nullable(),
    daChot: z.boolean(),
});
export const dongDoanhThuKenhSchema = z.object({
    channelId: z.string(),
    ten: z.string(),
    /** Tên Network đang trả tiền; `null` khi tiền đi AdSense trực tiếp. */
    network: z.string().nullable(),
    gross: z.number(),
    /** `null` = chưa đủ ngày có cả doanh thu và lượt xem ⇒ `—`. */
    rpm: z.number().nullable(),
    /** Phần trăm so kỳ trước; `null` khi kỳ trước không có tiền. */
    pct: z.number().nullable(),
});
export const dongNguonTienSchema = z.object({
    /** Id đối tác, hoặc `__adsense__` cho AdSense trực tiếp. */
    id: z.string(),
    nhan: z.string(),
    /** `network` hoặc `adsense` — giao diện phân biệt bằng chip. */
    loai: z.enum(['network', 'adsense']),
    gross: z.number(),
    phanTram: z.number(),
    soKenh: z.number().int().nonnegative(),
});
export const doanhThuNhieuKenhSchema = dauChungSchema.extend({
    o: z.array(oTomTatSchema),
    granularity: granularitySchema,
    cot: z.array(cotDoanhThuNgaySchema),
    theoKenh: z.array(dongDoanhThuKenhSchema),
    theoNguonTien: z.array(dongNguonTienSchema),
    currency: z.string(),
    /**
     * `true` khi kỳ còn kênh **chưa chốt** — ô "Thực nhận" mang chip "tạm tính".
     *
     * Đi thẳng từ `team.chiaTheoKenh`, không tự suy: số đem đi trả tiền chỉ chắc
     * khi mọi kênh đã chốt.
     */
    thucNhanTamTinh: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Chỉ số nào dẫn khối nào                                             */
/* ------------------------------------------------------------------ */
/**
 * Bốn tab dùng chung `?chi-so=` của tab Tổng quan hay không.
 *
 * **Không**: ba tab mới có khối riêng của chúng, không khối nào đổi theo chỉ số
 * đang chọn. `?chi-so=` chỉ có nghĩa ở tab Tổng quan, và giao diện giữ nó trên
 * URL khi chuyển tab để quay lại Tổng quan là đúng ô cũ.
 */
export const CHI_SO_CHI_CO_O_TONG_QUAN = true;
export { maChiSoSchema };
//# sourceMappingURL=hieuSuatNhieuKenh.js.map